import { useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Spinner from '../spinner/Spinner';
import { ApiContext } from '../../contexts/ApiContext';
import { catalogPath } from '../../config/catalogSections';
import { useCatalogAvailability } from '../../hooks/useCatalogAvailability';

/**
 * Selects de marca y producto.
 *
 * No tiene estado propio: el valor de cada select ES el parámetro de la URL, y
 * elegir una opción navega. Así desaparecen los tres efectos que antes
 * intentaban mantener sincronizados los selects con el estado del context.
 *
 * Según la sección puede ofrecer solo los equipos que tienen contenido cargado
 * (ver useCatalogAvailability). Lo que está en la URL nunca se esconde: si se
 * llegó por el buscador o por un link compartido, el select tiene que mostrar
 * ese equipo aunque no pase el filtro.
 */
export default function Filter({ section, brandSlug, productSlug }) {
    const { brands, productsByBrand, productsByBrandLoading, fetchProductsByBrand, emptyProductByBrand } = useContext(ApiContext);
    const navigate = useNavigate();
    const availability = useCatalogAvailability(section);

    // brands puede venir null (cargando) o con error, así que nunca se asume array.
    const brandList = useMemo(() => {
        const all = Array.isArray(brands) ? brands : [];
        if (!availability.brandSlugs) return all;
        return all.filter(brand =>
            availability.brandSlugs.has(brand.brand_slug) || brand.brand_slug === brandSlug
        );
    }, [brands, availability.brandSlugs, brandSlug]);

    const productList = useMemo(() => {
        const all = Array.isArray(productsByBrand) ? productsByBrand : [];
        if (!availability.productIds) return all;
        return all.filter(product =>
            availability.productIds.has(String(product.id)) || product.slug === productSlug
        );
    }, [productsByBrand, availability.productIds, productSlug]);

    // La marca de la URL manda: si cambia, se recargan sus productos.
    useEffect(() => {
        if (brandSlug) {
            fetchProductsByBrand(brandSlug);
        } else {
            emptyProductByBrand();
        }
    }, [brandSlug, fetchProductsByBrand, emptyProductByBrand]);

    const handleSelectChangeBrand = (event) => {
        // Al cambiar de marca se suelta el producto: ya no pertenece a esta marca.
        navigate(catalogPath(section, event.target.value));
    };

    const handleSelectChangeProduct = (event) => {
        navigate(catalogPath(section, brandSlug, event.target.value));
    };

    return (
        <>
            <h5>Seleccione una Marca</h5>
            <div className="row customFilter">
                <div className="col-12">
                    <StyleFilterInput>
                        <select
                            className="form-select"
                            value={brandSlug || ''}
                            onChange={handleSelectChangeBrand}
                        >
                            <option value="" disabled>Seleccione una marca</option>
                            {brandList.map(brand => (
                                <option key={brand.id} value={brand.brand_slug}>
                                    {brand.nombre}
                                </option>
                            ))}
                        </select>
                    </StyleFilterInput>
                </div>
            </div>
            {productsByBrandLoading ? (
                <Spinner />
            ) : (
                productList.length > 0 && (
                    <StyleFilterInput>
                        <h5 className="product-heading">Seleccione un Producto</h5>
                        <div className="row productFilter">
                            <div className="col-12">
                                <select
                                    value={productSlug || ''}
                                    onChange={handleSelectChangeProduct}
                                    className="form-select"
                                >
                                    <option value="" disabled>Seleccione un producto</option>
                                    {productList.map(product => (
                                        <option key={product.id} value={product.slug}>
                                            {product.nombre_producto}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </StyleFilterInput>
                )
            )}
        </>
    );
}


const StyleFilterInput = styled.div`

    .product-heading {

        padding-bottom: 0.6rem;
        margin-top: 2rem !important;
    }
`;
